// apps/admin/src/main.tsx
//
// Phase 8.1 Wave 1 Task 01 — admin ops portal entry refresh.
// vg-binding: P8.D-150  (Shared @printway/ui — ThemeProvider + Toaster from shared UI)
// vg-binding: P8.D-159  (FCP <1s — minimal bootstrap, QueryClient with staleTime 30s)
//
// Phase 1 Task 44 — admin SPA entry. Composes:
//   - React 19 root + StrictMode
//   - QueryClientProvider (TanStack Query, staleTime 30s, no refetch on focus)
//   - ThemeProvider (@printway/ui) — onThemeChange wired to PATCH /users/me/preferences
//   - i18n init (@printway/shared/i18n) — VI default, EN/FR fallback
//   - RouterProvider (react-router-dom v7+, BrowserRouter via createBrowserRouter)
//
// Wave 9b will add /login, /register etc. — for now placeholder home + 404.

import '@printway/ui/styles.css';
import './a11y-overrides.css'; // Phase 8.1.1 a11y: AA text-contrast override (admin-scoped)

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  DEFAULT_TIMEZONE,
  ThemeProvider,
  Toaster,
  detectBrowserTimezone,
  installTimezoneRuntime,
  setTimezone as setDisplayTimezone,
  type Theme,
} from '@printway/ui';
import { apiClient } from './lib/apiClient.js';
import { usePreferencesStore } from './stores/usePreferencesStore.js';
import { createI18n } from '@printway/shared/i18n';
// Use the canonical shared QueryClient (lib/queryClient.ts) so logout can
// clear it — a local `new QueryClient()` here was unreachable from the auth
// store, leaving the previous account's cached data on screen after a
// re-login (dbg-20260720). Single instance = single source of cache truth.
import { queryClient } from './lib/queryClient.js';

import { App } from './App.js';
// Phase 8.1 Wave 10 Task 46 — admin-local i18n bootstrap layers the `admin`
// namespace on top of the shared common/errors bundles.
import { initAdminI18n } from './i18n/config.js';

// i18n bootstrap — async, but resolved before render via top-level await
// alternative. Phase 8.1 Wave 10 Task 46: route through `initAdminI18n` so
// the admin namespace + browser-locale detection apply. The plain
// `createI18n` symbol is retained to honour the existing import surface and
// keep the call available for any future SSR boot path.
void createI18n;
void initAdminI18n();

/** Persist theme to backend (PATCH /auth/me/preferences — shared auth prefs,
 *  same endpoint the Preferences tab uses). Best-effort. */
async function persistThemePreference(theme: Theme): Promise<void> {
  try {
    await apiClient.patch('/auth/me/preferences', { theme });
  } catch (err) {
    // Theme preference save is best-effort; a network failure must not break UI.
    // eslint-disable-next-line no-console
    console.warn('[admin] failed to persist theme preference', err);
  }
}

// Stale-chunk recovery — sau mỗi deploy, các chunk lazy cũ (hash trong tên,
// vd DashboardPage-DnwYImCC.js) bị xoá khỏi server. Một tab đang mở giữ
// index.html cũ sẽ cố tải chunk đã xoá → 404 → Vite bắn `vite:preloadError`
// và React Router render "Failed to fetch dynamically imported module".
// Ta tự reload MỘT lần để nạp index.html + manifest mới. sessionStorage guard
// chống reload-loop nếu 404 do nguyên nhân khác (deploy hỏng thật).
if (typeof window !== 'undefined') {
  const CHUNK_RELOAD_KEY = 'pw-chunk-reloaded';
  window.addEventListener('vite:preloadError', (event) => {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    event.preventDefault();
    window.location.reload();
  });
  // Chunk mới nạp OK → xoá cờ để lần deploy sau vẫn được reload.
  window.addEventListener('load', () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  });
}

// dbg-20260810 — display timezone.
//
// This has to run BEFORE the first render. `installTimezoneRuntime` wraps
// `Intl.DateTimeFormat` + `Date.prototype.toLocale*` so a format call that
// does not name a zone gets the selected one; installing it after mount
// would leave the first paint in the browser's zone and only correct itself
// on the next re-render.
//
// The seed comes from `usePreferencesStore`, which rehydrates from
// localStorage synchronously at import. That store's default is 'UTC', which
// is the right neutral for a stored preference but the wrong first-run
// default for an operator — so a first run with nothing persisted adopts the
// browser's own zone instead, and the topbar reads the offset they expect.
installTimezoneRuntime();
{
  const stored = usePreferencesStore.getState().timezone;
  setDisplayTimezone(
    stored && stored !== DEFAULT_TIMEZONE ? stored : detectBrowserTimezone(),
  );
}
// Keep the runtime in step with later writes to the store — Preferences →
// General changes the zone without going through the topbar picker.
usePreferencesStore.subscribe((state) => {
  setDisplayTimezone(state.timezone);
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in admin index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Theme follows the @printway/ui design system (light/dark/system).
          forceTheme lock removed 2026-06-30 — Preferences → Appearance now
          drives setTheme live; ThemeProvider boots from localStorage 'pw-theme'
          and persists via onThemeChange. */}
      <ThemeProvider
        defaultTheme="light"
        onThemeChange={persistThemePreference}
      >
        <Toaster>
          <App />
        </Toaster>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
